/**
 * 見積詳細画面 (admin)。明細を表示しメール再送・公開URLコピー。
 */
import { ArrowLeft, Copy, ExternalLink, Mail } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useQuote, useSendQuoteMail } from '@/hooks/useQuotes';
import { paths } from '@/routes/paths';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

export function QuoteDetailPage() {
  const navigate = useNavigate();
  const { dealId, quoteId } = useParams<{ dealId: string; quoteId: string }>();
  const { data: quote, isLoading } = useQuote(dealId ?? null, quoteId ?? null);
  const sendMail = useSendQuoteMail();
  const [recipientOverride, setRecipientOverride] = useState('');

  if (isLoading) return <Spinner />;
  if (!quote)
    return (
      <Screen title="見積詳細">
        <p>見積が見つかりません</p>
      </Screen>
    );

  const publicUrl = `${window.location.origin}/quote/${quote.publicToken}`;

  const handleSend = async () => {
    try {
      const result = await sendMail.mutateAsync({
        dealId: quote.dealId,
        quoteId: quote.id,
        recipientEmail: recipientOverride.trim() || null,
      });
      if (result.mailSent) toast.success('見積メールを送信しました');
      else toast.error(`送信に失敗: ${result.reason ?? '不明なエラー'}`);
    } catch (err) {
      toast.error(`送信に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title={quote.title}
      description={`No.${quote.id.slice(0, 8)} / 発行日 ${fmtDate(quote.issuedAt)} / 有効期限 ${fmtDate(quote.validUntil)}`}
      actions={
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            // 直前に開いていた画面 (商談詳細 / 見積管理 / 顧客詳細) へ戻る。
            // アプリ内の遷移履歴が無い場合 (URL 直開き等) は商談ボードへ。
            const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
            if (idx > 0) navigate(-1);
            else navigate(paths.deals);
          }}
        >
          <ArrowLeft />
          戻る
        </Button>
      }
    >
      <div className="space-y-6 max-w-4xl">
        <div className="flex flex-wrap items-center gap-3">
          <Badge>{quote.status}</Badge>
          {quote.hideDiscounts === true && <Badge variant="draft">書類上は割引金額非表示</Badge>}
          {quote.hideModelNames === true && <Badge variant="draft">書類上は製品型番非表示</Badge>}
          <span className="text-sm text-[var(--text-muted)]">合計</span>
          <span className="text-2xl font-semibold tabular-nums">
            ¥{quote.totalIncludingTax.toLocaleString()}
          </span>
          <span className="text-sm text-[var(--text-muted)]">(税込)</span>
        </div>

        <section>
          <h3 className="text-sm font-semibold mb-2">明細</h3>
          <table className="w-full text-sm border border-[var(--border)]">
            <thead className="bg-[var(--bg-elevated)]">
              <tr>
                <th className="text-left p-2">商品</th>
                <th className="text-right p-2">単価 (税抜)</th>
                <th className="text-right p-2">数量</th>
                <th className="text-right p-2">小計 (税込)</th>
              </tr>
            </thead>
            <tbody>
              {quote.lineItems.map((it, i) => (
                <tr key={i} className="border-t border-[var(--border)]">
                  <td className="p-2">
                    {it.name}
                    {it.variation && (
                      <span className="ml-1 text-xs text-[var(--text-muted)]">
                        ({it.variation})
                      </span>
                    )}
                    {(it.discountAmountExcludingTax ?? 0) > 0 && (
                      <div className="text-xs text-[var(--accent-red,_#c0392b)]">
                        割引
                        {it.discountType === 'percent' && it.discountValue != null
                          ? ` (${it.discountValue}%)`
                          : ''}
                        : −¥
                        {(it.discountAmountExcludingTax ?? 0).toLocaleString()}
                        （税抜）
                      </div>
                    )}
                  </td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{it.priceExcludingTax.toLocaleString()}
                  </td>
                  <td className="text-right p-2 tabular-nums">{it.quantity}</td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{it.subtotalIncludingTax.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--bg-elevated)]">
              {(() => {
                const discountTotal = quote.lineItems.reduce(
                  (a, it) => a + (it.discountAmountExcludingTax ?? 0),
                  0,
                );
                if (discountTotal <= 0) return null;
                return (
                  <>
                    <tr>
                      <td colSpan={3} className="p-2 text-right">
                        割引前小計 (税抜)
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        ¥{(quote.subtotalExcludingTax + discountTotal).toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="p-2 text-right">
                        割引合計 (税抜)
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        −¥{discountTotal.toLocaleString()}
                      </td>
                    </tr>
                  </>
                );
              })()}
              <tr>
                <td colSpan={3} className="p-2 text-right">
                  小計 (税抜)
                </td>
                <td className="p-2 text-right tabular-nums">
                  ¥{quote.subtotalExcludingTax.toLocaleString()}
                </td>
              </tr>
              <tr>
                <td colSpan={3} className="p-2 text-right">
                  消費税
                </td>
                <td className="p-2 text-right tabular-nums">¥{quote.taxAmount.toLocaleString()}</td>
              </tr>
              {quote.hasTradeIn &&
                quote.tradeInType === 'discount' &&
                (quote.tradeInAmount ?? 0) > 0 && (
                  <tr>
                    <td colSpan={3} className="p-2 text-right">
                      下取り値引
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      −¥{(quote.tradeInAmount ?? 0).toLocaleString()}
                    </td>
                  </tr>
                )}
              <tr>
                <td colSpan={3} className="p-2 text-right font-semibold">
                  合計 (税込)
                </td>
                <td className="p-2 text-right tabular-nums font-semibold">
                  ¥{quote.totalIncludingTax.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">支払条件</h3>
          <p className="text-sm">
            {quote.paymentMethod === 'transfer' ? '振込' : 'クレジットカード'}
            {quote.installments ? ` / ${quote.installments}回払い` : ''}
            {quote.hasTradeIn ? ` / 下取り (${quote.tradeInType ?? '-'})` : ''}
          </p>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">公開URL</h3>
          <div className="flex items-center gap-2 max-w-2xl">
            <input
              readOnly
              value={publicUrl}
              className="flex-1 px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                toast.success('コピーしました');
              }}
            >
              <Copy />
              コピー
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.open(publicUrl, '_blank')}>
              <ExternalLink />
              開く
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">メール送信</h3>
          <div className="flex items-center gap-2 max-w-2xl">
            <input
              placeholder="送付先 (空欄なら顧客の既定メール)"
              value={recipientOverride}
              onChange={(e) => setRecipientOverride(e.target.value)}
              className="flex-1 px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
            />
            <Button
              type="button"
              variant="primary"
              onClick={handleSend}
              disabled={sendMail.isPending}
            >
              <Mail />
              {quote.sentAt ? '再送信' : '送信'}
            </Button>
          </div>
          {quote.sentAt && (
            <p className="text-xs text-[var(--text-muted)]">
              最終送信: {fmtDate(quote.sentAt)} / 宛先: {quote.sentTo}
            </p>
          )}
        </section>
      </div>
    </Screen>
  );
}
